import React, { useCallback, useEffect, useRef, useState } from 'react';
import TextField, { FilledTextFieldProps } from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import Grow from '@material-ui/core/Grow';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import clsx from 'clsx';

import { useGi18n } from '../hooks';

interface SearchInputProps extends Partial<Omit<FilledTextFieldProps, 'onChange'>> {
    onChange?(value: string): void;
    defaultValue?: string;
}

const SearchInput: React.FC<SearchInputProps> = ({ onChange = () => { }, defaultValue, InputProps: { classes: cl = {}, ...InputProps } = {}, inputProps: { maxLength, ...inputProps } = {}, ...rest }) => {
    const [searchValue, setSearchValue] = useState(defaultValue || '');

    const initialMount = useRef(true);

    const classes = useStyles();

    const placeholder = useGi18n()(i18n => i18n.error.search);

    const onValueCange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value);
    }

    const onSearch = useCallback(() => {
        onChange(searchValue || '');
    }, [searchValue, onChange]);

    const onCancel = useCallback(() => {
        setSearchValue('');
    }, [searchValue]);

    useEffect(() => {
        if (initialMount.current) {
            initialMount.current = false;
            return;
        }

        const timer = setTimeout(() => {
            onChange(searchValue);
        }, 400);

        return () => {
            clearTimeout(timer);
        }
    }, [searchValue]);

    return (
        <TextField
            type="text"
            autoComplete="off"
            variant="filled"
            value={searchValue}
            onChange={onValueCange}
            placeholder={placeholder}
            InputProps={{
                disableUnderline: true,
                classes: {
                    ...cl,
                    root: clsx(classes.inputRoot, cl.root),
                    input: clsx(classes.input, cl.input)
                },
                startAdornment: (
                    <IconButton color="primary" className={classes.iconButton} onClick={onSearch}>
                        <SearchIcon className={classes.icon} />
                    </IconButton>
                ),
                endAdornment: (
                    <Grow in={!!searchValue} timeout={100}>
                        <IconButton color="primary" className={classes.iconButton} onClick={onCancel}>
                            <CloseIcon className={classes.icon} />
                        </IconButton>
                    </Grow>
                ),
                ...InputProps
            }}
            inputProps={{
                ...inputProps,
                maxLength: maxLength ?? 250,
            }}
            {...rest}
        />
    );
}

const useStyles = makeStyles(theme => ({
    inputRoot: {
        borderRadius: 999,
        padding: '0 2.96px',
        backgroundColor: '#F5F5F5',

        transition: 'background-color 0.5s ease',

        '&:hover, &:focus-within': {
            backgroundColor: '#ECECEC',
        } as CSSProperties,
        [theme.breakpoints.down(500)]: {
            padding: '0 4px',
        } as CSSProperties,
        [theme.breakpoints.up(1600)]: {
            padding: '0 4px',
        } as CSSProperties
    },

    input: {
        padding: 0,
        color: theme.palette.primary.main,
        fontFamily: 'Assistant-Regular',
        fontSize: 17.77,

        [theme.breakpoints.down(500)]: {
            fontSize: 24,
        } as CSSProperties,
        [theme.breakpoints.up(1600)]: {
            fontSize: 24
        } as CSSProperties
    },

    iconButton: {
        padding: 8.8,

        [theme.breakpoints.down(500)]: {
            padding: 12,
        } as CSSProperties,
        [theme.breakpoints.up(1600)]: {
            padding: 12,
        } as CSSProperties
    },

    icon: {
        height: 17.77,
        width: 17.77,

        [theme.breakpoints.down(500)]: {
            height: 24,
            width: 24,
        } as CSSProperties,
        [theme.breakpoints.up(1600)]: {
            height: 24,
            width: 24,
        } as CSSProperties
    },
}));

export default SearchInput;